import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer } from "@/components/ui/responsive-container";
import { EnhancedPhotoModal } from "@/components/ui/enhanced-photo-modal";
import { ModernCamera } from "@/components/ui/modern-camera";
import { ModernPhotoGrid } from "@/components/ui/modern-photo-grid";
import { EnhancedUploadProgress } from "@/components/ui/enhanced-upload-progress";
import { useIsMobile } from "@/hooks/useMobile";
import {
  Camera,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Maximize2,
  Minimize2,
} from "lucide-react";
import type { IntakeData } from "@/lib/types";
import { cn } from "@/lib/utils";
import { BatchUploadService } from "@/lib/batchUploadService";
import { patientIntakeApi } from "@/lib/patientIntakeApi";
import type {
  BatchUploadResult,
  UploadProgress as UploadProgressType,
} from "@/lib/batchUploadService";

import { Trans, msg, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
 


interface ResponsivePhotoCaptureProps {
  data: IntakeData;
  onUpdate: (data: Partial<IntakeData>) => void;
  onNext: () => void;
  /** Previous intake step (shown below the “capture current photo” hint when that hint is visible). */
  onBack?: () => void;
  /**
   * When true (e.g. intake v2), Back on small screens is in the CardShell sticky footer — inline Back is `md+` only to avoid duplicate buttons.
   * Inline Back is always shown after Previous/Next on tablet/desktop (`md+`).
   */
  mobileBackUsesStickyFooter?: boolean;
}

const ORTHODONTIC_STEPS = [
  {
    id: "upper_occlusal",
    title: msg`Upper Occlusal`,
    description: msg`Open wide, tilt up, arch visible`,
    instruction: msg`Open your mouth wide and tilt your head slightly up so we can see your upper teeth.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F6b025284885f47fc936bf371991d2694?format=webp&width=800",
    category: "occlusal" as const,
    required: true,
  }, 
  { 
    id: "frontal_retracted_apart",
    title: msg`Frontal Retracted Apart`,
    description: msg`Pull lips back, teeth apart`,
    instruction: msg`Pull your lips back with your fingers and keep your teeth slightly apart.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2abffd7480e74b9c92484c3ecf5335c7?format=webp&width=800",
    category: "frontal" as const,
    required: true,
  },
  {
    id: "lower_occlusal",
    title: msg`Lower Occlusal`,
    description: msg`Open wide, tilt down, lower arch visible`,
    instruction: msg`Open your mouth wide and tilt your head down so we can see your lower teeth.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F1966745da916429eab37092509273047?format=webp&width=800",
    category: "occlusal" as const,
    required: true,
  },
  {
    id: "right_buccal",
    title: msg`Right Buccal`,
    description: msg`Right side bite view`,
    instruction: msg`Turn your head to the right, pull back your cheek, and bite down gently.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F03c7bf08876f4869a469602542118d7f?format=webp&width=800",
    category: "buccal" as const,
    required: true,
  },
  {
    id: "frontal_in_occlusion",
    title: msg`Frontal In Occlusion`,
    description: msg`Front view with teeth together`,
    instruction: msg`Face the camera, pull your lips back, and bite down with teeth together.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F2fb2c38e3a0f4649abc5fab535c553ce?format=webp&width=800",
    category: "frontal" as const,
    required: true,
  },
  {
    id: "left_buccal",
    title: msg`Left Buccal`,
    description: msg`Left side bite view`,
    instruction: msg`Turn your head to the left, pull back your cheek, and bite down gently.`,
    example:
      "https://cdn.builder.io/api/v1/image/assets%2Fb9f781aabe32414cb20a10fe7c362455%2F98a875b6214443c6b708dcbe6413cdf6?format=webp&width=800",
    category: "buccal" as const,
    required: true,
  },
];

export default function ResponsivePhotoCapture({
  data,
  onUpdate,
  onNext,
  onBack,
  mobileBackUsesStickyFooter = false,
}: ResponsivePhotoCaptureProps) {
  const { i18n } = useLingui();
  const translatedSteps = useMemo(() => ORTHODONTIC_STEPS.map(step => ({
    ...step,
    title: i18n._(step.title),
    description: i18n._(step.description),
    instruction: i18n._(step.instruction),
  })), [i18n]);
  const isMobile = useIsMobile();
  const [currentStepIndex, setCurrentStepIndex] = useState(() => {
    return ORTHODONTIC_STEPS.length > 0 ? 0 : -1;
  });
  
  const [capturedPhotos, setCapturedPhotos] = useState<Record<string, string>>(
    data.photos || {},
  );
  const capturedPhotosRef = useRef<Record<string, string>>(data.photos || {});
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    id: string;
    title: string;
    image: string;
    timestamp?: number;
  } | null>(null);
   
  const [showFullscreenCamera, setShowFullscreenCamera] = useState(isMobile);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureStatus, setCaptureStatus] = useState<
    "ready" | "capturing" | "processing" | "success" | "error"
  >("ready");
  const [captureMessage, setCaptureMessage] = useState("");
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<UploadProgressType | null>(null);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [failedPhotos, setFailedPhotos] = useState<string[]>([]);
  const [uploadResult, setUploadResult] = useState<BatchUploadResult | null>(
    null,
  );
  const batchUploadService = useRef<BatchUploadService | null>(null);
  useEffect(() => {
    batchUploadService.current = new BatchUploadService((progress) => {
      setUploadProgress(progress);
    });
    return () => {
      if (batchUploadService.current) {
        batchUploadService.current.cleanup();
      }
    };
  }, []);

  useEffect(() => {
    capturedPhotosRef.current = capturedPhotos;
  }, [capturedPhotos]);

  const dataUrlToFile = useCallback((dataUrl: string, filename: string): File => {
    const [meta = "", raw = ""] = dataUrl.split(",");
    const mime = meta.match(/:(.*?);/)?.[1] || "image/jpeg";
    const bytes = atob(raw);
    const array = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      array[i] = bytes.charCodeAt(i);
    }
    return new File([array], filename, { type: mime });
  }, []);

  const resolveFormUid = useCallback(async (): Promise<string> => {
    if (data.formUid) return data.formUid;
    const email = (data.email ?? "").trim().toLowerCase();
    if (!email) throw new Error("Missing formUid and email for photo upload");
    const formResponse = await patientIntakeApi.getIntakeFormByEmail(email);
    const formUid = formResponse?.data?.formUid;
    if (!formUid || typeof formUid !== "string") {
      throw new Error("Could not resolve formUid for photo upload");
    }
    return formUid;
  }, [data.formUid, data.email]);

  const uploadCapturedPhoto = useCallback(
    async (stepId: string, imageData: string, sequenceIndex: number) => {
      try {
        const formUid = await resolveFormUid();
        const file = dataUrlToFile(imageData, `${stepId}.jpg`);
        const upload = await patientIntakeApi.uploadPhoto(formUid, file, {
          photoPosition: stepId,
          photoSequence: sequenceIndex,
          replaceMediaId: data.photoMediaMap?.[stepId],
        });
        const media = upload.data?.media;
        if (!media?.id) return;

        const nextMediaMap = {
          ...(data.photoMediaMap ?? {}),
          [stepId]: media.id,
        };

        if (typeof media.downloadUrl === "string" && media.downloadUrl.length > 0) {
          const nextPhotos = {
            ...capturedPhotosRef.current,
            [stepId]: media.downloadUrl,
          };
          setCapturedPhotos(nextPhotos);
          onUpdate({
            photos: nextPhotos,
            photoMediaMap: nextMediaMap,
          });
          // Only revoke the queued blob after the UI uses a stable https URL.
          if (batchUploadService.current?.hasPhoto(stepId)) {
            batchUploadService.current.removePhoto(stepId);
          }
        } else {
          onUpdate({ photoMediaMap: nextMediaMap });
          // Keep blob preview alive when API does not return downloadUrl yet.
        }
      } catch (error) {
        console.warn("Immediate photo upload failed, keeping local preview", error);
      }
    },
    [resolveFormUid, dataUrlToFile, data.photoMediaMap, onUpdate],
  );

  const findNextIncompletePhotoIndex = useCallback(() => {
    const currentPhotos = capturedPhotosRef.current;
    console.log(
      "🔍 Finding next incomplete photo. Captured photos:",
      currentPhotos,
    );
    for (let i = 0; i < ORTHODONTIC_STEPS.length; i++) {
      const stepId = ORTHODONTIC_STEPS[i].id;
      const hasPhoto = !!currentPhotos[stepId];
      console.log(
        `  Step ${i} (${stepId}): ${hasPhoto ? "HAS PHOTO" : "MISSING PHOTO"}`,
      );
      if (!hasPhoto) {
        console.log(
          `✅ Found next incomplete photo at index ${i}: ${ORTHODONTIC_STEPS[i].title}`,
        );
        return i;
      }
    }
    console.log("✅ All photos are captured");
    return -1;
  }, []);

  const validCurrentStepIndex = Math.max(
    0,
    Math.min(currentStepIndex, ORTHODONTIC_STEPS.length - 1),
  );
  const totalRequiredPhotos = ORTHODONTIC_STEPS.length;
  const alreadyRegisteredPhotos = ORTHODONTIC_STEPS.filter(
    (step) => !!data.photoMediaMap?.[step.id],
  ).length;
  const adjustedUploadCurrent = Math.min(
    totalRequiredPhotos,
    alreadyRegisteredPhotos + (uploadProgress?.current ?? 0),
  );
  const currentStep = ORTHODONTIC_STEPS[validCurrentStepIndex];
  const progressPercent =
    ((validCurrentStepIndex +
      (currentStep && capturedPhotos[currentStep.id] ? 1 : 0)) /
      ORTHODONTIC_STEPS.length) *
    100;

  const handleBatchUpload = useCallback(async () => {
    if (!batchUploadService.current || isUploading) {
      return;
    }

    if (showFullscreenCamera) {
      setShowFullscreenCamera(false);
      interface DocumentWithFullscreen extends Document {
        webkitFullscreenElement?: Element | null;
        msFullscreenElement?: Element | null;
        webkitExitFullscreen?: () => Promise<void>;
        msExitFullscreen?: () => Promise<void>;
      }
      const doc = document as DocumentWithFullscreen;
      if (
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.msFullscreenElement
      ) {
        try {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (doc.webkitExitFullscreen) {
            await doc.webkitExitFullscreen();
          } else if (doc.msExitFullscreen) {
            await doc.msExitFullscreen();
          }
        } catch (error) {
          console.warn("Could not exit full screen mode:", error);
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setIsUploading(true);
    setShowUploadProgress(true);
    try {
      const result = await batchUploadService.current.uploadAllPhotos(
        data.email,
        data.formUid,
      );
      const finalPhotos = { ...capturedPhotos };
      Object.entries(result.uploadedPhotos).forEach(([photoId, url]) => {
        finalPhotos[photoId] = url;
      });
      setCapturedPhotos(finalPhotos);
      const updatedMediaMap = {
        ...(data.photoMediaMap ?? {}),
        ...result.mediaIds,
      };
      onUpdate({
        photos: finalPhotos,
        photoMediaMap: updatedMediaMap,
      });
      setUploadResult(result);
      if (result.success) {
        console.log("✅ All photos uploaded successfully!", result);
        setTimeout(() => {
          setShowUploadProgress(false);
          onNext();
        }, 2000);
      } else {
        console.warn("⚠️ Some photos failed to upload:", result.failedPhotos);
        setFailedPhotos(result.failedPhotos);
        const failedCount = result.failedPhotos.length;
        const successCount = Object.keys(result.uploadedPhotos).length;
        console.log(
          `📊 Upload Summary: ${successCount} successful, ${failedCount} failed`,
        );
      }
    } catch (error) {
      console.error("Batch upload error:", error);
      setShowUploadProgress(false);
      onNext();
    } finally {
      setIsUploading(false);
    }
  }, [
    batchUploadService,
    isUploading,
    capturedPhotos,
    onUpdate,
    onNext,
    data.email,
    data.formUid,
    data.photoMediaMap,
    showFullscreenCamera,
  ]);

  const handlePhotoCapture = useCallback(
    async (imageData: string) => {
      if (!currentStep || !batchUploadService.current) {
        console.error("No current step or batch upload service available");
        setCaptureStatus("error");
        setCaptureMessage(i18n._(msg`Error: Unable to capture photo. Please try again.`));
        return;
      }
      if (
        isCapturing ||
        captureStatus === "capturing" ||
        captureStatus === "processing"
      ) {
        return;
      }
      setIsCapturing(true);
      setCaptureStatus("capturing");
      setCaptureMessage(i18n._(msg`Capturing photo...`));
      try {

        if (!imageData || imageData === "data:," || imageData.length < 1000) {
          throw new Error("Invalid image data captured");
        }
        setCaptureStatus("processing");
        setCaptureMessage(i18n._(msg`Processing your photo...`));
        const sequenceIndex =
          ORTHODONTIC_STEPS.findIndex((step) => step.id === currentStep.id) + 1;
        const localUrl = batchUploadService.current.addPhoto(
          currentStep.id,
          imageData,
          {
            photoPosition: currentStep.id,
            photoSequence: sequenceIndex,
            originalFilename: `${currentStep.id}.jpg`,
            replaceMediaId: data.photoMediaMap?.[currentStep.id],
          },
        );
        const newPhotos = {
          ...capturedPhotos,
          [currentStep.id]: localUrl,
        };
        setCapturedPhotos(newPhotos);
        onUpdate({
          photos: newPhotos,
          [`${currentStep.id}_timestamp`]: Date.now(),
        });
        void uploadCapturedPhoto(currentStep.id, imageData, sequenceIndex);
        setCaptureStatus("success");
        setCaptureMessage(
          t`Great! ${i18n._(currentStep.title)} photo captured successfully.`
        );
        setShowSuccessAnimation(true);
        console.log(
          `📸 Photo captured and stored locally: ${currentStep?.title || "Unknown step"}`,
        );
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 1000);
        setIsTransitioning(true);
        setTimeout(() => {
          const currentPhotos = capturedPhotosRef.current;
          console.log(
            "🔄 Auto-progression triggered. Current photos:",
            Object.keys(currentPhotos),
          );
          const nextIncompleteIndex = findNextIncompletePhotoIndex();
          console.log("🔍 Next incomplete photo index:", nextIncompleteIndex);
          if (nextIncompleteIndex !== -1) {
            console.log(
              `➡️ Moving to step ${nextIncompleteIndex}: ${ORTHODONTIC_STEPS[nextIncompleteIndex]?.title}`,
            );
            setCurrentStepIndex(nextIncompleteIndex);
            setCaptureStatus("ready");
            setCaptureMessage("");
            setShowSuccessAnimation(false);
            setIsTransitioning(false);
          } else {
            console.log("🎉 All 6 photos captured! Starting batch upload...");
            setIsTransitioning(false);
            handleBatchUpload();
          }
        }, 1000);
      } catch (error) {
        console.error("Photo capture error:", error);
        setCaptureStatus("error");
        setCaptureMessage(i18n._(msg`Failed to capture photo. Please try again.`));
        try {
          const byteCharacters = atob(imageData.split(",")[1]);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "image/jpeg" });
          const localUrl = URL.createObjectURL(blob);
          const newPhotos = {
            ...capturedPhotos,
            [currentStep.id]: localUrl,
          };
          setCapturedPhotos(newPhotos);
          onUpdate({
            photos: newPhotos,
            [`${currentStep.id}_timestamp`]: Date.now(),
            [`${currentStep.id}_error`]: true,
          });
          setCaptureStatus("success");
          setCaptureMessage(i18n._(msg`Photo captured (with fallback processing)`));
        } catch (fallbackError) {
          console.error("Fallback photo processing failed:", fallbackError);
          setCaptureStatus("error");
          setCaptureMessage(
            i18n._(msg`Photo capture failed completely. Please try again.`),
          );
        }
      } finally {
        setIsCapturing(false);
      }
    },
    [
      currentStep,
      capturedPhotos,
      onUpdate,
      isCapturing,
      captureStatus,
      i18n,
      data.photoMediaMap,
      findNextIncompletePhotoIndex,
      handleBatchUpload,
      uploadCapturedPhoto,
    ],
  );

 
  const handleNextPhoto = useCallback(() => {
    if (!currentStep || !capturedPhotos[currentStep.id]) {
      setCaptureMessage(
        i18n._(msg`Please capture the current photo before proceeding to the next one.`),
      );
      return;
    }

    setIsTransitioning(true);
    setCaptureStatus("ready");
    setCaptureMessage("");
    setShowSuccessAnimation(false);
    const nextIncompleteIndex = findNextIncompletePhotoIndex();
    if (nextIncompleteIndex !== -1) {
      setCurrentStepIndex(nextIncompleteIndex);
    }
    setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
  }, [currentStep, capturedPhotos, findNextIncompletePhotoIndex, i18n]);

  const handlePreviousPhoto = useCallback(() => {
    if (validCurrentStepIndex > 0) {
      setIsTransitioning(true);
      setCurrentStepIndex((prev) => prev - 1);
      setCaptureStatus("ready");
      setCaptureMessage("");
      setShowSuccessAnimation(false);
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    }
  }, [validCurrentStepIndex]);


  const handleRetryFailedPhotos = useCallback(async () => {
    if (!batchUploadService.current || !uploadResult) {
      return;
    }

    setIsUploading(true);
    setFailedPhotos([]);
    try {
      const retryResult = await batchUploadService.current.uploadAllPhotos(
        data.email,
        data.formUid,
      );
      const mergedResult: BatchUploadResult = {
        success: retryResult.failedPhotos.length === 0,
        uploadedPhotos: {
          ...(uploadResult.uploadedPhotos ?? {}),
          ...retryResult.uploadedPhotos,
        },
        failedPhotos: retryResult.failedPhotos,
        mediaIds: {
          ...(uploadResult.mediaIds ?? {}),
          ...retryResult.mediaIds,
        },
        errors: {
          ...(uploadResult.errors ?? {}),
          ...retryResult.errors,
        },
      };

      setUploadResult(mergedResult);
      setFailedPhotos(mergedResult.failedPhotos);

      const finalPhotos = { ...capturedPhotos };
      Object.entries(retryResult.uploadedPhotos).forEach(([photoId, url]) => {
        finalPhotos[photoId] = url;
      });
      setCapturedPhotos(finalPhotos);

      const updatedMediaMap = {
        ...(data.photoMediaMap ?? {}),
        ...retryResult.mediaIds,
      };
      onUpdate({
        photos: finalPhotos,
        photoMediaMap: updatedMediaMap,
      });

      if (mergedResult.failedPhotos.length === 0) {
        setTimeout(() => {
          setShowUploadProgress(false);
          onNext();
        }, 2000);
      }
    } catch (error) {
      console.error("Retry failed:", error);
    } finally {
      setIsUploading(false);
    }
  }, [
    uploadResult,
    batchUploadService,
    capturedPhotos,
    data.photoMediaMap,
    data.email,
    data.formUid,
    onUpdate,
    onNext,
  ]);

  const handleContinueWithFailedPhotos = useCallback(() => {
    setShowUploadProgress(false);
    onNext();
  }, [onNext]);

  const handleComplete = useCallback(() => {
    const missingPhotos = ORTHODONTIC_STEPS.filter(
      (step) => !capturedPhotos[step.id],
    );
    if (missingPhotos.length > 0) {
      return;
    }

    // Safety: only allow continue when every captured dental photo has a registered media id.
    // This guarantees all 6 photos are actually uploaded, not only locally previewed.
    const photoMediaMap = data.photoMediaMap ?? {};
    const hasUnregisteredCapturedPhotos = ORTHODONTIC_STEPS.some(
      (step) => !!capturedPhotos[step.id] && !photoMediaMap[step.id],
    );
    if (hasUnregisteredCapturedPhotos) {
      handleBatchUpload();
      return;
    }

    const queuedCount = batchUploadService.current
      ? batchUploadService.current.getPhotoCount()
      : 0;
    if (queuedCount > 0) {
      handleBatchUpload();
      return;
    }
    onNext();
  }, [capturedPhotos, data.photoMediaMap, handleBatchUpload, onNext]);

  const openPhotoModal = useCallback(
    (stepId: string) => {
      const step = ORTHODONTIC_STEPS.find((s) => s.id === stepId);
      const photo = capturedPhotos[stepId];
      if (step && photo) {
        interface IntakeDataWithTimestamps extends IntakeData {
          [key: string]: unknown;
        }
        const dataWithTimestamps = data as IntakeDataWithTimestamps;
        setSelectedPhoto({
          id: stepId,
          title: i18n._(step.title),
          image: photo,
          timestamp: (dataWithTimestamps[`${stepId}_timestamp`] as number) || Date.now(),
        });
        setShowPhotoModal(true);
      }
    },
    [capturedPhotos, data, i18n],
  );

  const closePhotoModal = useCallback(() => {
    setShowPhotoModal(false);
    setSelectedPhoto(null);
  }, []);


  const retakePhoto = useCallback(
    (stepId: string) => {
      setIsTransitioning(true);
      const stepIndex = ORTHODONTIC_STEPS.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        setCurrentStepIndex(stepIndex);
        setCapturedPhotos((prev) => {
          const updated = { ...prev };
          delete updated[stepId];
          return updated;
        });
        closePhotoModal();
      }
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    },
    [closePhotoModal],
  );

  // Early return check after all hooks are defined
  if (ORTHODONTIC_STEPS.length === 0) {
    return (
      <ResponsiveContainer variant="wide" className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="text-center py-8">
          <p className="text-red-600">
            <Trans>Error: No photo steps configured</Trans>
          </p>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <>
      {/* Enhanced Photo Modal */}
      {selectedPhoto && (
        <EnhancedPhotoModal
          isOpen={showPhotoModal}
          onClose={closePhotoModal}
          photo={selectedPhoto}
          onRetake={() => retakePhoto(selectedPhoto.id)}
          showEditTools={true}
        />
      )}

      <ResponsiveContainer variant="full" className="py-4 sm:py-6 px-3 sm:px-6 lg:px-8 max-w-[1800px] w-full mx-auto">
        {/* Progress header — aligned with intake v2 (CardShell progress) */}
        <div className="mb-5 sm:mb-6">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 mb-3">
            <h2 className="text-[clamp(1.25rem,4.5vw,1.875rem)] font-bold text-gray-900 leading-tight">
              <Trans>Dental Photos</Trans>
            </h2>
            <span className="text-sm text-gray-500 tabular-nums">
              {Object.keys(capturedPhotos).length} / {ORTHODONTIC_STEPS.length}{" "}
              <Trans>photos</Trans>
            </span>
          </div>
          <div className="h-[3px] w-full overflow-hidden rounded-full bg-gray-200/90">
            <div
              className="h-full rounded-full transition-[width] duration-300 ease-out"
              style={{
                width: `${progressPercent}%`,
                background: "linear-gradient(90deg, #1a7faf, #166B93)",
                boxShadow: "0 0 6px rgba(22,107,147,0.35)",
              }}
            />
          </div>
        </div>

        {/* Desktop/Tablet: Side-by-side layout */}
        {!isMobile ? (
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* Modern Camera Section */}
            <div className="space-y-4">
              {/* Capture Status Display */}
              <Card
                className={cn(
                  "transition-all duration-300 rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]",
                  captureStatus === "success" &&
                    showSuccessAnimation &&
                    "border-green-200 bg-green-50/90",
                  captureStatus === "error" && "border-red-200 bg-red-50/90",
                  (captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) &&
                    "border-[#166B93]/25 bg-[#166B93]/[0.06]",
                )}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {captureStatus === "success" && (
                      <CheckCircle className="w-6 h-6 text-green-600 animate-pulse" />
                    )}
                    {captureStatus === "error" && (
                      <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 text-sm font-bold">!</span>
                      </div>
                    )}
                    {(captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) && (
                      <div className="w-6 h-6 bg-[#166B93]/15 rounded-full flex items-center justify-center animate-pulse">
                        <Camera className="w-4 h-4 text-[#166B93]" />
                      </div>
                    )}
                    {captureStatus === "ready" && !isTransitioning && (
                      <Camera className="w-6 h-6 text-[#166B93]" />
                    )}
                    <div>
                      <p
                        className={cn(
                          "font-medium",
                          captureStatus === "success" && "text-green-800",
                          captureStatus === "error" && "text-red-800",
                          (captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) && "text-[#166B93]",
                          captureStatus === "ready" && !isTransitioning && "text-gray-800",
                        )}
                      >
                        {isTransitioning
                          ? <Trans>Moving to next step...</Trans>
                          : captureMessage ||
                            t`Ready to capture: ${currentStep ? i18n._(currentStep.title) : "Photo"}`}
                      </p>
                      {captureStatus === "ready" && !isTransitioning && (
                        <p className="text-sm text-gray-500">
                          <Trans>Position yourself and click the capture button</Trans>
                        </p>
                      )}
                      {isTransitioning && (
                        <p className="text-sm text-[#166B93]/80">
                          <Trans>Please wait while we prepare the next step</Trans>
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <ModernCamera
                onCapture={handlePhotoCapture}
                isFullscreen={showFullscreenCamera}
                onToggleFullscreen={() =>
                  setShowFullscreenCamera(!showFullscreenCamera)
                }
                overlayGuide={{
                  width: "12rem",
                  height: "14rem",
                  shape: "oval",
                }}
                instructions={{
                  title: currentStep ? i18n._(currentStep.title) : "Photo Capture",
                  description: currentStep ? i18n._(currentStep.instruction) : i18n._(msg`Please position yourself for the photo`),
                  example: currentStep?.example || "",
                }}

                autoCapture={false}
                captureDelay={3}
                disabled={
                  isCapturing ||
                  captureStatus === "capturing" ||
                  captureStatus === "processing" ||
                  isTransitioning
                }
              />


              {/* Current Step Info Card */}
              <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <CardContent className="p-4 sm:p-5">
                  <div className="space-y-3">
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg text-gray-900">
                        {currentStep ? i18n._(currentStep.title) : i18n._(msg`Photo Capture`)}
                      </h3>
                      <p className="text-gray-500 text-sm mt-0.5">
                        {currentStep ? i18n._(currentStep.instruction) :
                          i18n._(msg`Please position yourself for the photo`)}
                      </p>
                    </div>

                    {/* Example Image */}
                    {currentStep?.example && (
                      <div className="flex items-center gap-3 p-3 bg-[#166B93]/[0.06] rounded-xl border border-[#166B93]/15">
                        <img
                          src={currentStep?.example || ""}
                          alt={currentStep ? `Example: ${i18n._(currentStep.title)}` : "Photo example"}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-800"><Trans>Example Photo</Trans></p>
                          <p className="text-xs text-gray-500">
                            <Trans>Position yourself like this</Trans>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Photography Tips */}
              <Card className="rounded-xl bg-white border border-gray-200 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <CardContent className="p-3 sm:p-4">
                  <h4 className="font-semibold text-sm text-gray-800 mb-2 flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5 text-[#166B93]" />
                    <Trans>Tips for a Great Photo:</Trans>
                  </h4>
                  <div className="space-y-1.5 text-xs text-gray-600">
                    <p className="flex items-start gap-1.5">
                      <span className="text-[#166B93] font-bold">•</span>
                      <span><Trans>Use natural light or bright indoor lighting</Trans></span>
                    </p>
                    <p className="flex items-start gap-1.5">
                      <span className="text-[#166B93] font-bold">•</span>
                      <span><Trans>Make sure your teeth/mouth are clearly visible</Trans></span>
                    </p>
                    <p className="flex items-start gap-1.5">
                      <span className="text-[#166B93] font-bold">•</span>
                      <span><Trans>Keep the camera steady</Trans></span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Modern Photos Grid Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-bold text-gray-900 tracking-tight">
                  <Trans>Photo Collection</Trans>
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFullscreenCamera(true)}
                  className="lg:hidden h-9 shrink-0 rounded-lg border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50"
                >
                  <Maximize2 className="w-4 h-4 mr-2" />
                  <Trans>Fullscreen</Trans>
                </Button>
              </div>

              <ModernPhotoGrid
                steps={translatedSteps}
                capturedPhotos={capturedPhotos}
                currentStepIndex={validCurrentStepIndex}
                onStepSelect={(index) => {
                
                  setIsTransitioning(true);

     
 
                  const targetStep = ORTHODONTIC_STEPS[index];
                  if (targetStep && capturedPhotos[targetStep.id]) {
                    const nextIncompleteIndex = findNextIncompletePhotoIndex();
                    if (nextIncompleteIndex !== -1) {
                      setCurrentStepIndex(nextIncompleteIndex);
                       setCaptureStatus("ready");
                      setCaptureMessage("");
            
                      setShowSuccessAnimation(false);
                    }
                  } else {
                    setCurrentStepIndex(index);
                 
                    setCaptureStatus("ready");
                    setCaptureMessage("");
                    
                    setShowSuccessAnimation(false);
                  }
 
                  setTimeout(() => {
                    setIsTransitioning(false);
                  }, 500);
                }}
                onPhotoView={openPhotoModal}
                onPhotoRetake={(stepId) => {
       
                  setIsTransitioning(true);

                  const stepIndex = ORTHODONTIC_STEPS.findIndex(
                    (s) => s.id === stepId,
                  );
                  if (stepIndex !== -1) {
                    setCurrentStepIndex(stepIndex);
                    setCapturedPhotos((prev) => {
                      const updated = { ...prev };
                      delete updated[stepId];
                      return updated;
                    });
                  }

                  // Clear transitioning state after a short delay
                  setTimeout(() => {
                    setIsTransitioning(false);
                  }, 500);
                }}
                showActions={true}
              />

              {/* Navigation — v2-style equal halves + gap (grid keeps row height aligned) */}
              {/* !m-0: src/index.css mobile `button + button { margin-top: 8px }` misaligns the 2nd control */}
              <div className="grid w-full grid-cols-2 gap-2 border-t border-gray-100 pt-4">
                <Button
                  variant="outline"
                  onClick={handlePreviousPhoto}
                  disabled={validCurrentStepIndex === 0}
                  className="!m-0 inline-flex h-12 min-h-12 w-full min-w-0 items-center justify-center rounded-lg border-gray-200 bg-white font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-45"
                >
                  <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
                  <Trans>Previous</Trans>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNextPhoto}
                  disabled={
                    validCurrentStepIndex === ORTHODONTIC_STEPS.length - 1 ||
                    !capturedPhotos[currentStep?.id || ""]
                  }
                  className="!m-0 inline-flex h-12 min-h-12 w-full min-w-0 items-center justify-center rounded-lg border-gray-200 bg-white font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-45"
                >
                  <Trans>Next</Trans>
                  <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                </Button>
              </div>

              {/* Leave step — always on desktop/tablet; on mobile, v2 sticky footer handles Back unless `mobileBackUsesStickyFooter` is false */}
              {onBack ? (
                <div
                  className={cn(
                    "w-full pt-2",
                    mobileBackUsesStickyFooter && "hidden md:block",
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onBack}
                    className="h-11 w-full justify-center rounded-lg border-gray-200 bg-white font-medium text-gray-800 shadow-sm hover:bg-gray-50"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
                    <Trans>Back</Trans>
                  </Button>
                </div>
              ) : null}

              {/* Next Photo Guidance */}
              {!capturedPhotos[currentStep?.id || ""] &&
                captureStatus === "ready" && (
                  <div className="text-center p-3 rounded-xl bg-[#166B93]/[0.06] border border-[#166B93]/20">
                    <p className="text-sm text-[#166B93] font-medium">
                      <Trans>
                        Please capture the current photo before proceeding to the
                        next one
                      </Trans>
                    </p>
                  </div>
                )}

              {/* Smart Navigation Info */}
              {Object.keys(capturedPhotos).length > 0 &&
                Object.keys(capturedPhotos).length <
                  ORTHODONTIC_STEPS.length && (
                  <div className="text-center p-3 bg-[#CEF12F]/10 rounded-lg border border-[#CEF12F]/40">
                    <p className="text-sm text-[#65751b] font-medium">
                      <Trans>
                        You can click any photo to jump between steps. We'll
                        automatically guide you to incomplete photos.
                      </Trans>
                    </p>
                  </div>
                )}

              {/* Complete Button */}
              {Object.keys(capturedPhotos).length ===
                ORTHODONTIC_STEPS.length && (
                <Button
                  onClick={handleComplete}
                  className="w-full h-12 rounded-lg font-semibold shadow-lg shadow-[#166B93]/20"
                  size="lg"
                  variant="jema"
                >
                  <Trans>Continue to Review</Trans>
                  <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Mobile: Stacked layout */
          <div className="space-y-6">
            {/* Camera mode — solid card for readability (v2 surfaces) */}
            <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-3.5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="min-w-0">
                <h3 className="font-semibold text-sm text-gray-900">
                  <Trans>Camera View</Trans>
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {showFullscreenCamera ? (
                    <Trans>Full screen mode</Trans>
                  ) : (
                    <Trans>Compact mode</Trans>
                  )}
                </p>
              </div>
              <Button
                onClick={() => setShowFullscreenCamera(!showFullscreenCamera)}
                variant="outline"
                size="sm"
                className="flex shrink-0 items-center gap-2 rounded-lg border-gray-200 bg-white h-10 px-3 font-medium text-gray-800 shadow-sm hover:bg-gray-50 hover:text-[#166B93] hover:border-[#166B93]/30"
              >
                {showFullscreenCamera ? (
                  <>
                    <Minimize2 className="w-4 h-4" />
                    <Trans>Compact</Trans>
                  </>
                ) : (
                  <>
                    <Maximize2 className="w-4 h-4" />
                    <Trans>Full Screen</Trans>
                  </>
                )}
              </Button>
            </div>

            {/* Mobile Capture Status Display */}
            <Card
              className={cn(
                "transition-all duration-300 rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden",
                captureStatus === "success" &&
                  showSuccessAnimation &&
                  "border-green-200 bg-green-50/90",
                captureStatus === "error" && "border-red-200 bg-red-50/90",
                (captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) &&
                  "border-[#166B93]/25 bg-[#166B93]/[0.06]",
              )}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-4">
                  {captureStatus === "success" && (
                    <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center animate-pulse flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                  )}
                  {captureStatus === "error" && (
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span className="text-red-600 font-bold text-lg">!</span>
                    </div>
                  )}
                  {(captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) && (
                    <div className="w-10 h-10 bg-[#166B93]/15 rounded-xl flex items-center justify-center animate-pulse flex-shrink-0 shadow-sm">
                      <Camera className="w-5 h-5 text-[#166B93]" />
                    </div>
                  )}
                  {captureStatus === "ready" && !isTransitioning && (
                    <div className="w-10 h-10 bg-[#166B93]/10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <Camera className="w-5 h-5 text-[#166B93]" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p
                      className={cn(
                        "font-semibold tracking-tight text-sm sm:text-base",
                        captureStatus === "success" && "text-green-800",
                        captureStatus === "error" && "text-red-800",
                        (captureStatus === "capturing" || captureStatus === "processing" || isTransitioning) && "text-[#166B93]",
                        captureStatus === "ready" && !isTransitioning && "text-gray-900",
                      )}
                    >
                      {isTransitioning
                        ? <Trans>Moving to next step...</Trans>
                        : captureMessage ||
                          (currentStep
                            ? t`Ready to capture: ${i18n._(currentStep.title)}`
                            : i18n._(msg`Ready to capture`))}
                    </p>
                    {captureStatus === "ready" && !isTransitioning && (
                      <p className="text-xs text-gray-500 font-medium mt-0.5">
                        <Trans>Position yourself and tap the capture button</Trans>
                      </p>
                    )}
                    {isTransitioning && (
                      <p className="text-xs text-[#166B93]/80 font-medium mt-0.5">
                        <Trans>Please wait while we prepare the next step</Trans>
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mobile Camera Section */}
            <ModernCamera
              onCapture={handlePhotoCapture}
              isFullscreen={showFullscreenCamera}
              onToggleFullscreen={() =>
                setShowFullscreenCamera(!showFullscreenCamera)
              }
              overlayGuide={{
                width: "12rem",
                height: "14rem",
                shape: "oval",
              }}
              

              instructions={{
                title: currentStep ? i18n._(currentStep.title) : i18n._(msg`Photo Capture`),
                description: currentStep ? i18n._(currentStep.instruction) : i18n._(msg`Please position yourself for the photo`),
                example: currentStep?.example || "",
              }}
              
              autoCapture={false}
              captureDelay={3}
              disabled={
                isCapturing ||
                captureStatus === "capturing" ||
                captureStatus === "processing" ||
                isTransitioning
              }
            />

            {/* Enhanced Photos Grid */}
            <div className="space-y-4">
              {/* Progress Summary */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                  <h3 className="font-bold text-gray-900 tracking-tight text-base sm:text-lg">
                    <Trans>Photo Progress</Trans>
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold tabular-nums text-gray-700 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200">
                      {Object.keys(capturedPhotos).length}/
                      {ORTHODONTIC_STEPS.length}
                    </div>
                    <div className="min-w-0 flex-1 sm:w-28 sm:flex-none h-2.5 bg-gray-200/90 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500 ease-out"
                        style={{
                          width: `${progressPercent}%`,
                          background: "linear-gradient(90deg, #1a7faf, #166B93)",
                          boxShadow: "0 0 6px rgba(22,107,147,0.35)",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Category Progress */}
                <div className="grid grid-cols-3 gap-1.5 sm:gap-3 text-[10px] sm:text-xs">
                  {[
                    { category: "frontal", label: msg`Front Views` },
                    { category: "occlusal", label: msg`Bite Views` },
                    { category: "buccal", label: msg`Side Views` },
                  ].map(({ category, label }) => {
                    const categorySteps = ORTHODONTIC_STEPS.filter(
                      (s) => s.category === category,
                    );
                    const completedInCategory = categorySteps.filter(
                      (s) => capturedPhotos[s.id],
                    ).length;
                    const done = completedInCategory === categorySteps.length;

                    return (
                      <div key={category} className="flex items-center gap-1.5 sm:gap-2">
                        <div className={cn(
                          "w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0",
                          done ? "bg-green-500" : "bg-[#166B93]"
                        )} />
                        <span className="text-gray-600 truncate">{i18n._(label)}</span>
                        <span className="font-bold text-gray-800 ml-auto">
                          {completedInCategory}/{categorySteps.length}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Photo Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {ORTHODONTIC_STEPS.map((step, index) => {
                  const isCompleted = !!capturedPhotos[step.id];
                  const isCurrent = index === validCurrentStepIndex;

                  return (
                    <div
                      key={step.id}
                      className={cn(
                        "relative group cursor-pointer transition-all duration-200 rounded-xl overflow-hidden touch-manipulation active:scale-[0.99] md:hover:scale-[1.01]",
                        "border border-gray-200 bg-white shadow-sm",
                        isCompleted && "border-green-200 bg-green-50/50",
                        isCurrent && "border-[#166B93]/60 bg-white shadow-md ring-1 ring-[#166B93]/20",
                        "md:hover:shadow-md",
                      )}
                      onClick={() => {
                        if (isCompleted) {
                         
                          const nextIncompleteIndex =
                            findNextIncompletePhotoIndex();
                          if (nextIncompleteIndex !== -1) {
                            
                            setIsTransitioning(true);
                            setCurrentStepIndex(nextIncompleteIndex);
                           
                            setCaptureStatus("ready");
                            setCaptureMessage("");
                           
                            setShowSuccessAnimation(false);
                          
                            setTimeout(() => {
                              setIsTransitioning(false);
                            }, 500);
                          } else {
                          
                            openPhotoModal(step.id);
                          }
                        } else {
                         
                          setIsTransitioning(true);
                          setCurrentStepIndex(index);
                         
                          setCaptureStatus("ready");
                          setCaptureMessage("");
                         
                          setShowSuccessAnimation(false);
                         
                          setTimeout(() => {
                            setIsTransitioning(false);
                          }, 500);
                        }
                      }}
                    >
                      {/* Step Number Badge */}
                      <div
                        className={cn(
                          "absolute top-1.5 left-1.5 z-10 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shadow-sm backdrop-blur-md",
                          isCompleted
                            ? "bg-green-500 text-white"
                            :                             isCurrent
                              ? "bg-[#166B93] text-white"
                              : "bg-black/40 text-white",
                        )}
                      >
                        {isCompleted ? "✓" : index + 1}
                      </div>

                      {/* Photo Area */}
                      <div className="aspect-[4/3] relative">
                        {isCompleted ? (
                          <>
                            <img
                              src={capturedPhotos[step.id]}
                              alt={i18n._(step.title)}
                              className="w-full h-full object-cover"
                            />
                            {/* Success Overlay */}
                            <div className="absolute inset-0 bg-green-500/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[1px]">
                              <div className="bg-white rounded-xl shadow-sm p-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <Camera
                              className={cn(
                                "w-7 h-7 mb-2 transition-colors",
                                isCurrent ? "text-[#166B93]" : "text-gray-400/80",
                              )}
                            />
                            {isCurrent && (
                              <div className="text-[10px] uppercase tracking-wider font-bold text-[#166B93] animate-pulse">
                                <Trans>Current</Trans>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Step Info */}
                      <div className="p-2 bg-white/60 backdrop-blur-sm border-t border-white/40 flex flex-col items-center justify-center min-h-[3.5rem]">
                        <p className="text-[10px] sm:text-xs font-bold text-center leading-tight text-gray-800 line-clamp-2">
                          {i18n._(step.title)}
                        </p>
                        {step.category && (
                          <div className="mt-1">
                            <span className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-bold bg-[#166B93]/10 text-[#166B93]">
                              {step.category}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Current Step Indicator */}
                      {isCurrent && !isCompleted && (
                        <div className="absolute inset-0 border-2 border-[#166B93]/50 rounded-xl animate-pulse pointer-events-none" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Mobile navigation — grid row; !m-0 overrides index.css `button + button { margin-top: 8px }` */}
              <div className="grid w-full grid-cols-2 gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handlePreviousPhoto}
                  disabled={validCurrentStepIndex === 0}
                  className="!m-0 inline-flex h-12 min-h-12 w-full min-w-0 items-center justify-center rounded-lg border-gray-200 bg-white font-semibold text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-45"
                >
                  <ArrowLeft className="mr-1.5 h-4 w-4 shrink-0" />
                  <Trans>Previous</Trans>
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNextPhoto}
                  disabled={
                    validCurrentStepIndex === ORTHODONTIC_STEPS.length - 1 ||
                    !capturedPhotos[currentStep?.id || ""]
                  }
                  className={cn(
                    "!m-0 inline-flex h-12 min-h-12 w-full min-w-0 items-center justify-center rounded-lg border font-semibold shadow-sm transition-colors disabled:opacity-45",
                    !capturedPhotos[currentStep?.id || ""]
                      ? "border-gray-200 bg-gray-50 text-gray-400"
                      : "border-[#166B93]/30 bg-[#166B93]/[0.08] text-[#166B93] hover:bg-[#166B93]/15",
                  )}
                >
                  <Trans>Next</Trans>
                  <ArrowRight
                    className={cn(
                      "ml-1.5 h-4 w-4 shrink-0",
                      !capturedPhotos[currentStep?.id || ""] ? "text-gray-400" : "text-[#166B93]",
                    )}
                  />
                </Button>
              </div>

              {onBack ? (
                <div
                  className={cn(
                    "w-full pt-2",
                    mobileBackUsesStickyFooter && "hidden md:block",
                  )}
                >
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onBack}
                    className="h-11 w-full justify-center rounded-lg border-gray-200 bg-white font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
                    <Trans>Back</Trans>
                  </Button>
                </div>
              ) : null}

              {/* Mobile Next Photo Guidance */}
              {!capturedPhotos[currentStep?.id || ""] &&
                captureStatus === "ready" && (
                  <div className="mt-2 rounded-xl border border-[#166B93]/20 bg-[#166B93]/[0.06] p-3.5 text-center">
                    <p className="text-sm font-medium leading-snug text-[#166B93]">
                      <Trans>
                        Please capture the current photo before proceeding to
                        the next one
                      </Trans>
                    </p>
                  </div>
                )}

              {/* Quick Actions */}
              {Object.keys(capturedPhotos).length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center pt-2">
                  <div className="text-xs font-semibold text-gray-600 bg-white/80 border border-gray-200 px-4 py-1.5 rounded-xl shadow-sm">
                    {t`${Object.keys(capturedPhotos).length} photos captured`}
                  </div>
                  {Object.keys(capturedPhotos).length <
                    ORTHODONTIC_STEPS.length && (
                    <div className="text-xs font-semibold text-[#166B93] bg-[#166B93]/[0.08] border border-[#166B93]/20 px-3 py-1.5 rounded-lg">
                      {t`${ORTHODONTIC_STEPS.length -
                          Object.keys(capturedPhotos).length} remaining`}
                    </div>
                  )}
                </div>
              )}

              {/* Smart Navigation Info */}
              {Object.keys(capturedPhotos).length > 0 &&
                Object.keys(capturedPhotos).length <
                  ORTHODONTIC_STEPS.length && (
                  <div className="text-center p-3.5 mt-2 rounded-xl bg-[#CEF12F]/12 border border-[#CEF12F]/35">
                    <p className="text-sm text-[#5c6b16] font-medium leading-relaxed">
                      <Trans>
                        You can tap any photo to jump between steps. We'll
                        automatically guide you to incomplete photos.
                      </Trans>
                    </p>
                  </div>
                )}
            </div>

            {/* Photography Instructions - Mobile */}
            <Card className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.04)] overflow-hidden">
              <CardContent className="p-4 sm:p-5">
                <h4 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4 text-[#166B93] shrink-0" />
                  <Trans>Tips for a Great Photo:</Trans>
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <p className="flex items-start gap-2">
                    <span className="text-[#166B93] font-bold mt-0.5">•</span>
                    <span className="leading-relaxed"><Trans>Use natural light or bright indoor lighting</Trans></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-[#166B93] font-bold mt-0.5">•</span>
                    <span className="leading-relaxed"><Trans>Make sure your teeth/mouth are clearly visible</Trans></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="text-[#166B93] font-bold mt-0.5">•</span>
                    <span className="leading-relaxed"><Trans>Keep the camera steady</Trans></span>
                  </p>
                </div>
              </CardContent>
            </Card>
            {/* Complete Button */}
            {Object.keys(capturedPhotos).length === ORTHODONTIC_STEPS.length && (
              <Button
                onClick={handleComplete}
                className="w-full min-h-12 h-12 rounded-lg text-base font-semibold shadow-lg shadow-[#166B93]/25"
                variant="jema"
              >
                <Trans>Continue to Review</Trans>
                <ArrowRight className="w-5 h-5 ml-2 shrink-0" />
              </Button>
            )}
          </div>
        )}
      </ResponsiveContainer>

      {/* Modern Fullscreen Camera */}
      {showFullscreenCamera && (
        <ModernCamera
          onCapture={handlePhotoCapture}
          onClose={() => setShowFullscreenCamera(false)}
          isFullscreen={true}
          overlayGuide={{
            width: "14rem",
            height: "16rem",
            shape: "oval",
          }}
          instructions={{
            title: currentStep ? i18n._(currentStep.title) : i18n._(msg`Photo Capture`),
            description: currentStep ? i18n._(currentStep.instruction) : i18n._(msg`Please position yourself for the photo`),
            example: currentStep?.example || "",
          }}

          autoCapture={false}
          captureDelay={3}
        />
      )}

      {/* Enhanced Upload Progress Modal (portal to body for true viewport centering) */}
      {showUploadProgress &&
        uploadProgress &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
            <div className="w-full max-w-[min(42rem,100vw-1.5rem)] sm:max-w-lg relative z-[201]">
              <EnhancedUploadProgress
                current={adjustedUploadCurrent}
                total={totalRequiredPhotos}
                currentPhoto={uploadProgress.currentPhoto}
                status={uploadProgress.status}
                failedPhotos={failedPhotos}
                uploadMode="parallel"
                uploadSpeed={uploadProgress.uploadSpeed}
                estimatedTimeRemaining={uploadProgress.estimatedTimeRemaining}
                totalSize={uploadProgress.totalSize}
                uploadedSize={uploadProgress.uploadedSize}
                startTime={uploadProgress.startTime}
                individualProgress={
                  uploadProgress.individualProgress as unknown as Record<
                    string,
                    {
                      status: "uploading" | "completed" | "pending" | "failed";
                      progress: number;
                    }
                  >
                }
                onRetryFailedPhotos={handleRetryFailedPhotos}
                onContinue={handleContinueWithFailedPhotos}
              />
            </div>
          </div>,
          document.body,
        )}

      
    </>
  );
}